import { useCallback, useEffect, useState } from "react";
import { Activity, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getTodayReadings, type Reading, type TodayReadings } from "@/lib/api";
import { ageOf, flagFor, flagTone, fmtNum, recordedByLine } from "@/lib/vitals";
import RecordVitalsDialog from "./RecordVitalsDialog";

interface Props {
    patientId: string;
    patientName: string;
    doctorId?: string | null;
    visitId?: string | null;
    canRecord: boolean;
    /** Patient's age in months — under 18 the H/L flags use paediatric ranges */
    ageMonths?: number | null;
    /** Called after a new set is saved (e.g. to refresh the queue's "Vitals done" tag) */
    onRecorded?: () => void;
}

const TILES: { code: "bp" | "pulse" | "temp" | "spo2" | "weight" | "height" | "rbs"; label: string; unit: string }[] = [
    { code: "bp", label: "BP", unit: "mmHg" },
    { code: "pulse", label: "Pulse", unit: "bpm" },
    { code: "temp", label: "Temp", unit: "°F" },
    { code: "spo2", label: "SpO2", unit: "%" },
    { code: "weight", label: "Weight", unit: "kg" },
    { code: "height", label: "Height", unit: "cm" },
    { code: "rbs", label: "RBS", unit: "mg/dL" },
];

/** Today's vitals for a patient at the front desk, with Record vitals. */
export default function TodayVitalsCard({ patientId, patientName, doctorId, visitId, canRecord, ageMonths = null, onRecorded }: Props) {
    const [data, setData] = useState<TodayReadings | null>(null);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        getTodayReadings(patientId).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
    }, [patientId]);
    useEffect(load, [load]);

    const t = data?.today ?? {};
    const tiles = TILES.map(tile => {
        if (tile.code === "bp") {
            const s = t.bp_systolic, d = t.bp_diastolic;
            return s && d ? { ...tile, value: `${fmtNum(s.value)}/${fmtNum(d.value)}`, flag: flagFor(s, ageMonths) ?? flagFor(d, ageMonths) } : null;
        }
        const r = t[tile.code];
        return r ? { ...tile, value: fmtNum(r.value), flag: flagFor(r, ageMonths) } : null;
    }).filter(Boolean) as { code: string; label: string; unit: string; value: string; flag: "H" | "L" | null }[];

    const newest = Object.values(t).sort((a, b) => (b!.recordedAt > a!.recordedAt ? 1 : -1))[0];
    const lastWeight = data?.last.weight;

    return (
        <div className="rounded-lg border border-border overflow-hidden">
            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/30 border-b border-border">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                    <Activity className="w-3.5 h-3.5" /> Today's vitals
                </p>
                {canRecord && (
                    <Button size="sm" variant={tiles.length ? "ghost" : "default"} className="h-7 text-xs gap-1" onClick={() => setOpen(true)} disabled={loading}>
                        <Plus className="w-3 h-3" /> {tiles.length ? "Record again" : "Record vitals"}
                    </Button>
                )}
            </div>

            {loading ? (
                <div className="flex justify-center py-5"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
            ) : tiles.length ? (
                <div className="p-3 space-y-2">
                    {/* Tiles fit the panel's width; the unit sits under the value so "152/96 H" never overflows */}
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(5.5rem,1fr))] gap-2">
                        {tiles.map(tile => (
                            <div key={tile.code} className="rounded-md bg-muted/40 px-2.5 py-1.5 min-w-0">
                                <p className="text-2xs uppercase tracking-wide text-muted-foreground">{tile.label}</p>
                                <p className={cn("text-sm font-semibold tabular-nums whitespace-nowrap", flagTone(tile.flag, tile.code))}>
                                    {tile.value}
                                    {tile.flag && <span className="ml-1 text-2xs font-bold">{tile.flag}</span>}
                                </p>
                                <p className="text-2xs text-muted-foreground">{tile.unit}</p>
                            </div>
                        ))}
                    </div>
                    {newest && <p className="text-2xs text-muted-foreground">Last recorded by {recordedByLine(newest)}</p>}
                </div>
            ) : (
                <div className="px-3 py-3 text-xs text-muted-foreground">
                    Not recorded today.
                    {lastWeight && <> Last weight {fmtNum(lastWeight.value)} kg, {ageOf(lastWeight.effectiveAt)}.</>}
                </div>
            )}

            <RecordVitalsDialog
                open={open}
                onOpenChange={setOpen}
                patientId={patientId}
                patientName={patientName}
                doctorId={doctorId}
                visitId={visitId}
                today={data}
                onSaved={next => { setData(next); onRecorded?.(); }}
            />
        </div>
    );
}
